/**
 * Zod schemas of the API payloads (docs/08 §6), shared by api (validation) and web (forms).
 * Instants are ISO 8601 UTC (`...Z`); domain dates `YYYY-MM-DD`; times `HH:MM`.
 */
import { z } from 'zod';
import {
  DOC_STATUSES,
  INVALIDATE_ENTITIES,
  MOVEMENT_TYPE_IDS,
  MOVEMENT_TYPES,
  ROLES,
  SIGNATURE_ROLES,
  SIGNED_QUANTITY_TYPE_IDS,
  TRAFFIC_LEVELS,
  CHECKLIST_PHASES,
} from './constants.js';
import type { MovementTypeId } from './types.js';

/** Italian validation messages for every zod schema in the process. */
export function configureZodItalian(): void {
  z.config(z.locales.it());
}

// ---------------------------------------------------------------- primitives

/** ISO 8601 UTC instant (`Z` suffix, any sub-second precision). */
export const isoDateTimeSchema = z.iso.datetime();
export const isoDateSchema = z.iso.date();
export const hhmmSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Ora non valida (HH:MM)');
export const idSchema = z.coerce.number().int().positive();

const INVALID_ID = 'Identificativo non valido';

/**
 * Optional reference id (point, user…): absent, `null` and empty string → `null`; otherwise a
 * positive integer (also as a numeric string). `idSchema.nullable()` must not be used for a field
 * that may be left out: `z.coerce` turns `undefined` into `NaN`. Inside `.partial()` (updates) a
 * field left out stays `undefined` ("unchanged"), while `null` or `''` clear it.
 */
export const nullableIdSchema = z.preprocess(
  (v) => (v === undefined || v === null || (typeof v === 'string' && !v.trim()) ? null : v),
  z.coerce
    .number({ error: INVALID_ID })
    .int({ error: INVALID_ID })
    .positive({ error: INVALID_ID })
    .nullable(),
);
export const quantitySchema = z.int().min(-1_000_000).max(1_000_000);
export const nonNegativeIntSchema = z.int().min(0).max(1_000_000);
export const roleSchema = z.enum(ROLES);
export const trafficLevelSchema = z.enum(TRAFFIC_LEVELS);
export const docStatusSchema = z.enum(DOC_STATUSES);
export const movementTypeIdSchema = z.literal(MOVEMENT_TYPE_IDS, {
  error: 'Tipo di movimento non valido',
});
export const invalidateEntitySchema = z.enum(INVALIDATE_ENTITIES);

/** Optional free text: trims, empty string → null. */
const optionalText = (max = 500) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v ? v : null));

const requiredText = (max = 200) => z.string().trim().min(1).max(max);

export const passwordSchema = z
  .string()
  .min(10, 'La password deve avere almeno 10 caratteri')
  .max(200);
export const pinSchema = z.string().regex(/^\d{4,6}$/, 'Il PIN deve avere da 4 a 6 cifre');

// ---------------------------------------------------------------- common params / queries

export const idParamsSchema = z.object({ id: idSchema });
export const dayParamsSchema = z.object({ day: isoDateSchema });
export const atQuerySchema = z.object({ at: isoDateTimeSchema.optional() });

// ---------------------------------------------------------------- auth

export const loginSchema = z.object({
  username: requiredText(100),
  password: z.string().min(1).max(200),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const passwordChangeSchema = z.object({
  current: z.string().min(1).max(200),
  next: passwordSchema,
});
export type PasswordChangeInput = z.infer<typeof passwordChangeSchema>;

export const pinSetSchema = z.object({ pin: pinSchema });
export type PinSetInput = z.infer<typeof pinSetSchema>;

export const pinVerifySchema = z.object({ pin: pinSchema });
export type PinVerifyInput = z.infer<typeof pinVerifySchema>;

// ---------------------------------------------------------------- settings

export const settingsSchema = z.object({
  eventName: requiredText(),
  tpcName: requiredText(),
  tpcAddress: optionalText(),
  tpcVat: optionalText(100),
  fmfName: requiredText(),
  fmfAddress: optionalText(),
  fmfVat: optionalText(100),
  warehouseName: requiredText(),
  hubName: requiredText(),
  responsibleName: requiredText(),
  responsiblePhone: requiredText(50),
  goodsDescription: requiredText(500),
  reasonPickup: requiredText(500),
  reasonReturn: requiredText(500),
  reasonDefective: requiredText(500),
  piecesPerBox: z.int().positive().nullable(),
  safetyStockPct: z.number().min(0).max(1),
  maxReportIntervalMin: z
    .int()
    .min(10)
    .max(24 * 60),
  readonlyShowsQuantities: z.boolean(),
  signatureEnabled: z.boolean(),
});
export type SettingsInput = z.infer<typeof settingsSchema>;

export const settingsUpdateSchema = settingsSchema.partial();
export type SettingsUpdateInput = z.infer<typeof settingsUpdateSchema>;

// ---------------------------------------------------------------- points, days, persons

export const pointSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^[A-Z]{2,4}$/, 'Codice punto: 2–4 lettere maiuscole'),
  name: requiredText(100),
  services: requiredText(200),
  responsibleName: optionalText(100),
  phone: optionalText(50),
  sortOrder: z.int().min(0).max(999),
  active: z.boolean(),
});
export type PointInput = z.infer<typeof pointSchema>;

/** `PUT /points/:id`: any subset of the fields. */
export const pointUpdateSchema = pointSchema.partial();
export type PointUpdateInput = z.infer<typeof pointUpdateSchema>;

export const pointHoursSchema = z.object({
  pointId: idSchema,
  openTime: hhmmSchema.nullable(),
  closeTime: hhmmSchema.nullable(),
  note: optionalText(200),
});
export type PointHoursInput = z.infer<typeof pointHoursSchema>;

export const operatingDaySchema = z.object({
  date: isoDateSchema,
  label: requiredText(100),
  active: z.boolean(),
  notes: optionalText(),
  hours: z.array(pointHoursSchema),
});
export type OperatingDayInput = z.infer<typeof operatingDaySchema>;

export const daysUpdateSchema = z.object({ days: z.array(operatingDaySchema).min(1) });
export type DaysUpdateInput = z.infer<typeof daysUpdateSchema>;

export const personSchema = z.object({
  organization: requiredText(100),
  name: requiredText(100),
  roleLabel: requiredText(100),
  pointId: nullableIdSchema,
  chainOfCommand: z.boolean(),
  phone: optionalText(50),
  userId: nullableIdSchema,
});
export type PersonInput = z.infer<typeof personSchema>;

/** `PUT /persons/:id`: any subset of the fields. */
export const personUpdateSchema = personSchema.partial();
export type PersonUpdateInput = z.infer<typeof personUpdateSchema>;

// ---------------------------------------------------------------- users

const userBaseSchema = z.object({
  /** Login name: 3–50 characters (letters, digits, `.`, `_`, `-`), unique ignoring case. */
  username: z
    .string()
    .trim()
    .min(3)
    .max(50)
    .regex(/^[a-z0-9._-]+$/i, 'Solo lettere, numeri, punto, trattino e trattino basso'),
  displayName: requiredText(100),
  role: roleSchema,
  pointId: nullableIdSchema,
  active: z.boolean(),
});

const pointRequiredForRole = <T extends { role?: string; pointId?: number | null }>(
  value: T,
  ctx: z.RefinementCtx,
) => {
  if (value.role === 'punto' && value.pointId == null) {
    ctx.addIssue({
      code: 'custom',
      path: ['pointId'],
      message: 'Il punto è obbligatorio per il ruolo responsabile di punto',
    });
  }
};

/**
 * `password` omitted → the API generates a temporary one and returns it once; `active` defaults
 * to true.
 */
export const userCreateSchema = userBaseSchema
  .extend({ password: passwordSchema.optional(), active: z.boolean().default(true) })
  .superRefine(pointRequiredForRole);
export type UserCreateInput = z.infer<typeof userCreateSchema>;

export const userUpdateSchema = userBaseSchema.partial().superRefine(pointRequiredForRole);
export type UserUpdateInput = z.infer<typeof userUpdateSchema>;

// ---------------------------------------------------------------- movement types

export const movementTypeUpdateSchema = z.object({
  name: requiredText(100).optional(),
  fromLabel: requiredText(100).optional(),
  toLabel: requiredText(100).optional(),
  docTitle: requiredText(100).optional(),
  docLabel: requiredText(100).optional(),
  active: z.boolean().optional(),
});
export type MovementTypeUpdateInput = z.infer<typeof movementTypeUpdateSchema>;

// ---------------------------------------------------------------- movements

const movementFieldsSchema = z.object({
  occurredAt: isoDateTimeSchema,
  typeId: movementTypeIdSchema,
  pointId: nullableIdSchema,
  quantity: quantitySchema,
  packages: z.int().min(0).max(100_000).nullable().optional(),
  deliveredBy: optionalText(100),
  receivedBy: optionalText(100),
  vehicle: optionalText(100),
  signed: z.boolean().default(false),
  notes: optionalText(1000),
  forcedOperatingDay: isoDateSchema.nullable().optional(),
  /** Admin only, unique. */
  forcedDocNumber: z.string().trim().min(1).max(30).nullable().optional(),
});

const requiresPoint = (typeId: number) =>
  MOVEMENT_TYPES.find((t) => t.id === typeId)?.requiresPoint ?? false;

const checkMovementRules = (
  value: { typeId?: number; pointId?: number | null; quantity?: number },
  ctx: z.RefinementCtx,
) => {
  if (value.typeId == null) return;
  if (requiresPoint(value.typeId) && value.pointId == null) {
    ctx.addIssue({
      code: 'custom',
      path: ['pointId'],
      message: 'Il punto è obbligatorio per questo tipo',
    });
  }
  if (value.quantity != null) {
    const signed = SIGNED_QUANTITY_TYPE_IDS.includes(value.typeId as MovementTypeId);
    if (signed && value.quantity === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['quantity'],
        message: 'La rettifica non può essere 0',
      });
    }
    if (!signed && value.quantity <= 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['quantity'],
        message: 'La quantità deve essere positiva',
      });
    }
  }
};

/**
 * `pointId` may also be left out (types without a point): absent → `null` (`nullableIdSchema`). In
 * the update schema the field stays optional, since there `undefined` means "unchanged".
 */
export const movementCreateSchema = movementFieldsSchema.superRefine(checkMovementRules);
export type MovementCreateInput = z.infer<typeof movementCreateSchema>;

/**
 * The type cannot change; `version` is mandatory (409 if stale). `signed` is redeclared without
 * default (see `reportUpdateSchema`): a `PUT` that leaves it out keeps the stored value.
 */
export const movementUpdateSchema = movementFieldsSchema
  .omit({ typeId: true })
  .extend({ signed: z.boolean() })
  .partial()
  .extend({ version: z.int().min(0) });
export type MovementUpdateInput = z.infer<typeof movementUpdateSchema>;

export const movementCancelSchema = z.object({ reason: requiredText(500) });
export type MovementCancelInput = z.infer<typeof movementCancelSchema>;

export const movementStatusSchema = z.object({ docStatus: docStatusSchema });
export type MovementStatusInput = z.infer<typeof movementStatusSchema>;

export const signatureSchema = z.object({
  role: z.enum(SIGNATURE_ROLES),
  signerName: requiredText(100),
  pngDataUrl: z
    .string()
    .regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/, 'Firma non valida')
    .max(2_000_000),
});
export type SignatureInput = z.infer<typeof signatureSchema>;

const booleanQuery = z
  .enum(['true', 'false', '1', '0'])
  .transform((v) => v === 'true' || v === '1');

export const movementsQuerySchema = z.object({
  day: isoDateSchema.optional(),
  pointId: idSchema.optional(),
  typeId: z.coerce.number().pipe(movementTypeIdSchema).optional(),
  status: docStatusSchema.optional(),
  anomalies: booleanQuery.optional(),
  /** true = only cancelled movements, false = only valid ones; omitted = all. */
  cancelled: booleanQuery.optional(),
});
export type MovementsQuery = z.infer<typeof movementsQuerySchema>;

/** `GET /print/blank/ddt?typeId`: variant of the blank DDT (default 1 = pickup, as the workbook). */
export const printBlankDdtQuerySchema = z.object({
  typeId: z.coerce
    .number()
    .pipe(z.literal([1, 4, 10], { error: 'Tipo di DDT non valido' }))
    .optional(),
});
export type PrintBlankDdtQuery = z.infer<typeof printBlankDdtQuerySchema>;

/** `GET /print/blank/verbale?typeId`: title and checklist of a type; omitted = generic form. */
export const printBlankVerbaleQuerySchema = z.object({
  typeId: z.coerce
    .number()
    .pipe(z.literal([2, 3, 5, 6, 7, 8, 9], { error: 'Tipo di verbale non valido' }))
    .optional(),
});
export type PrintBlankVerbaleQuery = z.infer<typeof printBlankVerbaleQuerySchema>;

// ---------------------------------------------------------------- reports

const reportFieldsSchema = z.object({
  reportedAt: isoDateTimeSchema,
  pointId: idSchema,
  progressiveDistributed: nonNegativeIntSchema,
  countedStock: nonNegativeIntSchema.nullable().optional(),
  trafficLevel: trafficLevelSchema.default('VERDE'),
  filledBy: optionalText(100),
  sentToEventLeads: z.boolean().default(false),
  notes: optionalText(1000),
});

/**
 * Override of the suspension block (docs/08 §7): only admin/supervisor may record, change or cancel
 * a report with growing public deliveries while the level is ARANCIONE/ROSSO; `force` defaults to
 * false and `force=true` needs a non-empty `forceReason`, which goes to the audit log.
 */
const reportForceSchema = z.object({
  force: z.boolean().default(false),
  forceReason: optionalText(500),
});

export const FORCE_REASON_REQUIRED =
  'Per forzare il report durante la sospensione è obbligatorio indicare il motivo';

const requireForceReason = (
  value: { force?: boolean; forceReason?: string | null },
  ctx: z.RefinementCtx,
) => {
  if (value.force && !value.forceReason) {
    ctx.addIssue({ code: 'custom', path: ['forceReason'], message: FORCE_REASON_REQUIRED });
  }
};

/*
 * Each report write has two schemas with the same fields:
 * - `report*BodySchema`: the request body validated by the API, without the force refinement (the
 *   API answers a missing reason with a 400 whose message names it, `assertForceReason`, instead
 *   of the generic "Dati non validi" of the validation errors);
 * - `report*Schema`: the same with the refinement, for the forms and every other client.
 */

/**
 * `POST /reports`: `reportedAt` omitted = now; `trafficLevel` omitted = level of the traffic light
 * at `reportedAt` (set by the API).
 */
export const reportCreateBodySchema = reportFieldsSchema
  .extend({
    reportedAt: isoDateTimeSchema.optional(),
    trafficLevel: trafficLevelSchema.optional(),
  })
  .extend(reportForceSchema.shape);
export const reportCreateSchema = reportCreateBodySchema.superRefine(requireForceReason);
export type ReportCreateInput = z.infer<typeof reportCreateSchema>;

/**
 * zod 4 applies `.default()` even inside `.partial()`: the defaulted fields are redeclared without
 * default, so a field left out of a `PUT` stays unchanged instead of being reset. `force` is added
 * after `.partial()`: it is not a stored field.
 */
export const reportUpdateBodySchema = reportFieldsSchema
  .extend({ trafficLevel: trafficLevelSchema, sentToEventLeads: z.boolean() })
  .partial()
  .extend({ version: z.int().min(0) })
  .extend(reportForceSchema.shape);
export const reportUpdateSchema = reportUpdateBodySchema.superRefine(requireForceReason);
export type ReportUpdateInput = z.infer<typeof reportUpdateSchema>;

/** Cancelling an earlier report may raise later increments: same override as the writes. */
export const reportCancelBodySchema = z
  .object({ reason: requiredText(500) })
  .extend(reportForceSchema.shape);
export const reportCancelSchema = reportCancelBodySchema.superRefine(requireForceReason);
export type ReportCancelInput = z.infer<typeof reportCancelSchema>;

// ---------------------------------------------------------- reports of every point at once

export const REPORT_BATCH_EMPTY = 'Inserisci il progressivo di almeno un punto';
export const REPORT_BATCH_POINT_REPEATED = 'Lo stesso punto compare più di una volta';

/** One row of the batch: the point and its progressive; counted stock and notes are optional. */
export const reportBatchItemSchema = z.object({
  pointId: idSchema,
  progressiveDistributed: nonNegativeIntSchema,
  countedStock: nonNegativeIntSchema.nullable().optional(),
  notes: optionalText(1000),
});
export type ReportBatchItem = z.infer<typeof reportBatchItemSchema>;

/**
 * `POST /reports/batch` (admin, supervisor; docs/05 §5, from v1.0.8): the 2-hour reports of every
 * point entered together. The header applies to every row (`reportedAt` omitted = now,
 * `trafficLevel` omitted = level at `reportedAt`, `filledBy`, `sentToEventLeads`, `notes` unless
 * the row has its own); each row becomes one ordinary point report, computed and checked by the
 * same rules as `POST /reports`; the rows are saved in one transaction, or none. `force` and
 * `forceReason` cover every row blocked by the suspension (ARANCIONE/ROSSO).
 */
export const reportBatchBodySchema = z
  .object({
    reportedAt: isoDateTimeSchema.optional(),
    trafficLevel: trafficLevelSchema.optional(),
    filledBy: optionalText(100),
    sentToEventLeads: z.boolean().default(false),
    notes: optionalText(1000),
    items: z.array(reportBatchItemSchema).min(1, { error: REPORT_BATCH_EMPTY }).max(100),
  })
  .extend(reportForceSchema.shape)
  .superRefine((value, ctx) => {
    const seen = new Set<number>();
    value.items.forEach((item, index) => {
      if (seen.has(item.pointId)) {
        ctx.addIssue({
          code: 'custom',
          path: ['items', index, 'pointId'],
          message: REPORT_BATCH_POINT_REPEATED,
        });
      }
      seen.add(item.pointId);
    });
  });
export const reportBatchSchema = reportBatchBodySchema.superRefine(requireForceReason);
export type ReportBatchInput = z.infer<typeof reportBatchSchema>;

export const reportsMarkSentSchema = z.object({ ids: z.array(idSchema).min(1) });
export type ReportsMarkSentInput = z.infer<typeof reportsMarkSentSchema>;

export const reportsQuerySchema = z.object({
  date: isoDateSchema.optional(),
  pointId: idSchema.optional(),
  /** Only reports whose control starts with ⚠. */
  anomalies: booleanQuery.optional(),
});
export type ReportsQuery = z.infer<typeof reportsQuerySchema>;

/** Languages of the texts shared with TPC (`ContentLanguage`). */
export const CONTENT_LANGUAGES = ['it', 'en'] as const;
export const contentLanguageSchema = z.enum(CONTENT_LANGUAGES);

/**
 * `GET /reports/summary`: stocks are added only for admin/supervisor; English by default (the
 * text goes to the TPC Event Leads).
 */
export const reportsSummaryQuerySchema = z.object({
  at: isoDateTimeSchema.optional(),
  includeStock: booleanQuery.optional(),
  lang: contentLanguageSchema.default('en'),
});
export type ReportsSummaryQuery = z.infer<typeof reportsSummaryQuerySchema>;

export const eventLeadsMessageSchema = z.object({
  text: requiredText(5000),
  reportIds: z.array(idSchema).default([]),
});
export type EventLeadsMessageInput = z.infer<typeof eventLeadsMessageSchema>;

// ---------------------------------------------------------------- traffic light

const trafficFieldsSchema = z.object({
  occurredAt: isoDateTimeSchema,
  level: trafficLevelSchema,
  scopePointId: nullableIdSchema,
  declaredBy: optionalText(100),
  receivedBy: optionalText(100),
  communicatedAt: isoDateTimeSchema.nullable().optional(),
  cardsSecured: z.boolean().default(false),
  notes: optionalText(1000),
});

export const trafficCreateSchema = trafficFieldsSchema;
export type TrafficCreateInput = z.infer<typeof trafficCreateSchema>;

/** Defaulted fields redeclared without default (see `reportUpdateSchema`). */
export const trafficUpdateSchema = trafficFieldsSchema
  .extend({ cardsSecured: z.boolean() })
  .partial();
export type TrafficUpdateInput = z.infer<typeof trafficUpdateSchema>;

export const trafficConfirmSchema = z.object({ pointId: idSchema, confirmed: z.boolean() });
export type TrafficConfirmInput = z.infer<typeof trafficConfirmSchema>;

export const trafficSecuredSchema = z.object({ cardsSecured: z.boolean() });
export type TrafficSecuredInput = z.infer<typeof trafficSecuredSchema>;

export const trafficQuerySchema = z.object({ day: isoDateSchema.optional() });
export type TrafficQuery = z.infer<typeof trafficQuerySchema>;

// ---------------------------------------------------------------- stock

/** `GET /stock/history`: samples every `step` minutes from `from` to `to` (default: last 24 h). */
export const stockHistoryQuerySchema = z.object({
  from: isoDateTimeSchema.optional(),
  to: isoDateTimeSchema.optional(),
  step: z.coerce.number().int().min(5).max(1440).default(60),
});
export type StockHistoryQuery = z.infer<typeof stockHistoryQuerySchema>;

// ---------------------------------------------------------------- closure, planning, checklist

export const closureCountSchema = z.object({
  hubCounted: nonNegativeIntSchema,
  countedBy: requiredText(100),
  countedAt: isoDateTimeSchema,
  /** Omitted = unchanged; null or empty = cleared. */
  notes: optionalText(1000).optional(),
});
export type ClosureCountInput = z.infer<typeof closureCountSchema>;

/** `POST /closure/:day/unlock` (admin): optional reason, written to the audit log. */
export const closureUnlockSchema = z.object({ reason: optionalText(500) });
export type ClosureUnlockInput = z.infer<typeof closureUnlockSchema>;

/** `forecasts`: forecast per point id (object keys are point ids). */
export const planningUpdateSchema = z.object({
  forecasts: z.record(z.string().regex(/^\d+$/, 'Id punto non valido'), nonNegativeIntSchema),
});
export type PlanningUpdateInput = z.infer<typeof planningUpdateSchema>;

/** Partial update: an omitted field is unchanged, null or empty clears it. */
export const checklistItemUpdateSchema = z
  .object({
    done: z.boolean(),
    /** Default when `done` becomes true: now. */
    doneAt: isoDateTimeSchema.nullable(),
    initials: optionalText(10),
    docRef: optionalText(100),
    notes: optionalText(1000),
  })
  .partial();
export type ChecklistItemUpdateInput = z.infer<typeof checklistItemUpdateSchema>;

export const checklistItemCreateSchema = z.object({
  text: requiredText(500),
  timeHint: optionalText(30),
  section: optionalText(50),
  responsible: optionalText(100),
  notes: optionalText(1000),
});
export type ChecklistItemCreateInput = z.infer<typeof checklistItemCreateSchema>;

export const checklistTemplateSchema = z.object({
  id: idSchema.optional(),
  phase: z.enum(CHECKLIST_PHASES),
  /** Longer than an item's: e.g. "22:00, dopo il rientro di Bacchettoni", `{CHIUSURA}`. */
  timeHint: optionalText(50),
  text: requiredText(500),
  /** Omitted, null or empty = template for every point. */
  pointId: nullableIdSchema,
  sortOrder: z.int().min(0).max(9999),
  active: z.boolean(),
  section: optionalText(50),
  responsible: optionalText(100),
});
export type ChecklistTemplateInput = z.infer<typeof checklistTemplateSchema>;

export const checklistTemplatesUpdateSchema = z.object({
  templates: z.array(checklistTemplateSchema),
});
export type ChecklistTemplatesUpdateInput = z.infer<typeof checklistTemplatesUpdateSchema>;

// ---------------------------------------------------------------- archive, export, audit

/** Movement documents listed by the archive (planimetries are not movement documents). */
export const ARCHIVE_FILE_KINDS = ['pdf', 'scan', 'signature'] as const;

export const archiveQuerySchema = z.object({
  day: isoDateSchema.optional(),
  typeId: z.coerce.number().pipe(movementTypeIdSchema).optional(),
  pointId: idSchema.optional(),
  /** Only movements with at least one file of this kind (and only those files). */
  kind: z.enum(ARCHIVE_FILE_KINDS).optional(),
  /** Document status of the movement. */
  status: docStatusSchema.optional(),
});
export type ArchiveQuery = z.infer<typeof archiveQuerySchema>;

/** `GET /archive/zip`: one operating day; omitted = the whole archive. */
export const archiveZipQuerySchema = z.object({ day: isoDateSchema.optional() });
export type ArchiveZipQuery = z.infer<typeof archiveZipQuerySchema>;

export const EXPORT_SCOPES = [
  'all',
  'movements',
  'reports',
  'traffic',
  'closure',
  'planning',
  'checklist',
  'audit',
] as const;

export const exportQuerySchema = z.object({
  scope: z.enum(EXPORT_SCOPES).default('all'),
  /** Reference instant of the "Riepilogo giornaliero" (default: now, like the Cruscotto). */
  at: isoDateTimeSchema.optional(),
  /**
   * Language of sheet names, headers and labels (default Italian, the workbook's, which the import
   * reads back); the values (point names, outcomes, traffic levels) are the engine's in both.
   */
  lang: contentLanguageSchema.default('it'),
});
export type ExportQuery = z.infer<typeof exportQuerySchema>;

export const AUDIT_MAX_PAGE_SIZE = 200;

export const auditQuerySchema = z.object({
  entity: z.string().max(50).optional(),
  entityId: z.string().max(50).optional(),
  userId: idSchema.optional(),
  from: isoDateTimeSchema.optional(),
  to: isoDateTimeSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  action: z.string().max(50).optional(),
  /** Rows per page (default 50); larger values are capped at `AUDIT_MAX_PAGE_SIZE`. */
  pageSize: z.coerce
    .number()
    .int()
    .min(1)
    .default(50)
    .transform((n) => Math.min(n, AUDIT_MAX_PAGE_SIZE)),
});
export type AuditQuery = z.infer<typeof auditQuerySchema>;

// ---------------------------------------------------------------- import

/** `POST /import/demo`: `replace=true` empties the operational registers first. */
export const importDemoQuerySchema = z.object({ replace: booleanQuery.default(false) });
export type ImportDemoQuery = z.infer<typeof importDemoQuerySchema>;

/**
 * `POST /import/xlsx`: `dryRun` (default true) only previews; `replace` empties the operational
 * registers first, `append` adds to existing data (without either, the registers must be empty).
 */
export const importXlsxQuerySchema = z
  .object({
    dryRun: booleanQuery.default(true),
    replace: booleanQuery.default(false),
    append: booleanQuery.default(false),
  })
  .refine((q) => !(q.replace && q.append), {
    path: ['append'],
    message: 'Scegliere "sostituisci" oppure "aggiungi", non entrambi',
  });
export type ImportXlsxQuery = z.infer<typeof importXlsxQuerySchema>;

// ---------------------------------------------------------------- SSE

export const invalidateEventSchema = z.object({ entities: z.array(invalidateEntitySchema) });
